import { Button, Select, Stack, TextInput } from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { fetchAuthSession } from "aws-amplify/auth";
import { useMemo } from "react";
import { client } from "@/api-client";
import type { TrainerPersonalInfoInput } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { Header } from "@/components/Header";
import { UniversalRoutes, useNavigator } from "@/routers/routes";
import { useUser } from "@/UserContext";
import { getCurrentCountry } from "@/utils/country";

// TrainerPersonalInfoInput is now imported directly from @/api-types

const PersonalDataWhenLoaded = ({
  data,
}: {
  data?: TrainerPersonalInfoInput;
}) => {
  const { navigate } = useNavigator();

  const geocodingLibrary = useMapsLibrary("geocoding");
  const geocoding = useMemo(
    () => geocodingLibrary && new geocodingLibrary.Geocoder(),
    [geocodingLibrary],
  );

  const form = useForm<TrainerPersonalInfoInput>({
    mode: "uncontrolled",
    initialValues: data
      ? {
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          locality: data.locality,
          address: data.address,
          postalCode: data.postalCode,
          country: data.country,
          state: data.state,
        }
      : {
          firstName: "",
          lastName: "",
          phone: "",
          locality: "",
          address: "",
          postalCode: "",
          country: getCurrentCountry(),
          state: "",
        },
    validate: {
      firstName: isNotEmpty("First name is required"),
      lastName: isNotEmpty("Last name is required"),
      phone: isNotEmpty("Phone number is required"),
      locality: isNotEmpty("City is required"),
      address: isNotEmpty("Address is required"),
      postalCode: isNotEmpty("Postal code is required"),
    },
  });

  const updatePersonalData = client.useMutation(
    "post",
    "/trainer/profile/personal-info",
  );

  const handleSubmit = async (
    values: Omit<TrainerPersonalInfoInput, "acceptedToc">,
  ) => {
    let location: { lat: number; lng: number } | undefined;

    if (geocoding) {
      try {
        const { results } = await geocoding.geocode({
          address: [
            values.address,
            values.postalCode,
            values.locality,
            values.country,
          ].join(" "),
        });
        const result = results?.[0];
        if (result) {
          location = {
            lat: result.geometry.location.lat(),
            lng: result.geometry.location.lng(),
          };
        }
      } catch (error) {
        console.error("Geocoding error:", error);
      }
    }

    await updatePersonalData.mutateAsync({
      body: {
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        address: values.address,
        postalCode: values.postalCode,
        country: values.country,
        state: values.state,
        locality: values.locality,
        location,
      },
    });

    if (!data) {
      await fetchAuthSession({ forceRefresh: true });
    }

    setTimeout(() => {
      navigate({ path: UniversalRoutes.PROFILE.HOME });
    }, 1);
  };

  return (
    <Stack gap="xs">
      <Header title="Personal Information" />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            withAsterisk
            label="First name"
            autoComplete="given-name"
            key={form.key("firstName")}
            {...form.getInputProps("firstName")}
          />
          <TextInput
            withAsterisk
            label="Last name"
            autoComplete="family-name"
            key={form.key("lastName")}
            {...form.getInputProps("lastName")}
          />
          <TextInput
            withAsterisk
            label="Phone"
            autoComplete="tel"
            key={form.key("phone")}
            {...form.getInputProps("phone")}
          />
          <TextInput
            withAsterisk
            label="Country"
            key={form.key("country")}
            defaultValue={getCurrentCountry()}
            disabled
            {...form.getInputProps("country")}
          />
          <Select
            label="Province"
            key={form.key("state")}
            {...form.getInputProps("state")}
            data={[
              { value: "NH", label: "Noord-Holland" },
              { value: "ZH", label: "Zuid-Holland" },
              { value: "GR", label: "Groningen" },
              { value: "FR", label: "Friesland" },
              { value: "DR", label: "Drenthe" },
              { value: "OV", label: "Overijssel" },
              { value: "GE", label: "Gelderland" },
              { value: "LI", label: "Limburg" },
              { value: "NB", label: "Noord-Brabant" },
              { value: "UT", label: "Utrecht" },
            ]}
          />
          <TextInput
            withAsterisk
            label="Address"
            autoComplete="street-address"
            key={form.key("address")}
            {...form.getInputProps("address")}
          />
          <TextInput
            withAsterisk
            label="City"
            autoComplete="address-level2"
            key={form.key("locality")}
            {...form.getInputProps("locality")}
          />
          <TextInput
            withAsterisk
            label="Postal code"
            autoComplete="postal-code"
            key={form.key("postalCode")}
            {...form.getInputProps("postalCode")}
          />
          <Button
            type="submit"
            fullWidth
            loading={updatePersonalData.isPending}
          >
            Save
          </Button>
        </Stack>
      </form>
    </Stack>
  );
};

export const PersonalDataLoadExisting = () => {
  const user = useUser();
  const { data } = client.useQuery(
    "get",
    "/trainer/profile/personal-data",
    {
      enabled: user.isProfileCreated,
    },
    {
      staleTime: 0,
      gcTime: 0,
    },
  );

  if (!data) {
    return <FancyLoader />;
  }

  return <PersonalDataWhenLoaded data={data} />;
};

export const Personal = PersonalDataWhenLoaded;

export const PersonalData = () => {
  return <PersonalDataLoadExisting />;
};
